import {
  Mic2,
  Sparkles,
  CalendarClock,
  RotateCcw,
  Repeat2,
  Check,
  Zap,
  type LucideIcon,
} from "lucide-react";

/* ─────────────── Hero product preview ─────────────── */

export function HeroProductPreview() {
  return (
    <div className="relative w-full">
      <div className="rounded-2xl border border-ink-200/70 bg-white shadow-xl">
        <FakeWindowChrome title="BrandEcho · New project" />
        <div className="grid gap-3 p-4 sm:grid-cols-5 sm:p-5">
          <div className="sm:col-span-2">
            <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">
              Long-form input
            </p>
            <div className="mt-2 rounded-xl border border-ink-200/60 bg-ink-50 p-3.5">
              <FakeLine width="100%" />
              <FakeLine width="92%" />
              <FakeLine width="78%" />
              <FakeLine width="88%" />
              <FakeLine width="64%" />
              <FakeLine width="44%" muted />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-lg border border-ink-200/60 bg-white px-3 py-2">
              <span className="text-2xs font-medium text-ink-500">
                1 credit · 5 variants
              </span>
              <span className="inline-flex items-center gap-1 rounded-md bg-brand-gradient px-2 py-1 text-2xs font-medium text-white">
                <Zap className="h-3 w-3" />
                Generate
              </span>
            </div>
          </div>
          <div className="sm:col-span-3">
            <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">
              5 native variants
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <PreviewTile
                accent="bg-ink-900"
                label="X / Twitter"
                lines={3}
                content={[
                  "Most teams ship slow because they don't…",
                  "1/ Stop chasing perfection",
                  "2/ Define done in writing",
                ]}
              />
              <PreviewTile
                accent="bg-sky-500"
                label="Bluesky"
                lines={3}
                content={[
                  "Most teams ship slow because they",
                  "don't define 'done'. Write it down.",
                ]}
              />
              <PreviewTile
                accent="bg-blue-700"
                label="LinkedIn"
                lines={4}
                content={[
                  "Most teams aren't actually slow.",
                  "They're undefined.",
                  "Here's what changes when you write down…",
                ]}
              />
              <PreviewTile
                accent="bg-amber-500"
                label="Newsletter"
                lines={3}
                content={[
                  "Issue 47 — Define done.",
                  "How three sentences saved our…",
                ]}
              />
              <PreviewTile
                accent="bg-rose-500"
                label="Short-video"
                lines={3}
                full
                content={[
                  "Hook (5s): Most teams aren't slow.",
                  "Body: Watch what 3 sentences do.",
                  "CTA: Pin it where the team writes.",
                ]}
              />
            </div>
          </div>
        </div>
      </div>
      <FloatingChip
        className="-bottom-4 -right-4 sm:-right-6"
        Icon={Mic2}
        label="Voice: punchy ops"
      />
      <FloatingChip
        className="-left-4 top-12 hidden sm:flex"
        Icon={Check}
        label="Saved 5s ago"
        tone="success"
      />
    </div>
  );
}

function FakeWindowChrome({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-ink-200/60 bg-ink-50/60 px-4 py-2.5">
      <div className="flex gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
        <span className="h-2.5 w-2.5 rounded-full bg-ink-200" />
      </div>
      <span className="ml-2 text-2xs font-medium text-ink-500">{title}</span>
    </div>
  );
}

function FakeLine({
  width,
  muted,
}: {
  width: string;
  muted?: boolean;
}) {
  return (
    <div
      className={`my-1.5 h-1.5 rounded-full ${muted ? "bg-ink-100" : "bg-ink-200/80"}`}
      style={{ width }}
    />
  );
}

function PreviewTile({
  accent,
  label,
  content,
  full,
}: {
  accent: string;
  label: string;
  content: string[];
  lines?: number;
  full?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border border-ink-200/60 bg-white p-2.5 ${full ? "col-span-2" : ""}`}
    >
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className={`h-1.5 w-5 rounded-full ${accent}`} />
        <p className="text-2xs font-semibold text-ink-700">{label}</p>
      </div>
      <div className="space-y-1">
        {content.map((c, i) => (
          <p key={i} className="text-[10.5px] leading-snug text-ink-600">
            {c}
          </p>
        ))}
      </div>
    </div>
  );
}

function FloatingChip({
  className = "",
  Icon,
  label,
  tone = "brand",
}: {
  className?: string;
  Icon: LucideIcon;
  label: string;
  tone?: "brand" | "success";
}) {
  const styles =
    tone === "success"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200/80"
      : "bg-white text-ink-700 border-ink-200/80";
  return (
    <div
      className={`absolute z-10 flex items-center gap-1.5 rounded-full border ${styles} px-2.5 py-1.5 text-xs font-medium shadow-md backdrop-blur-sm ${className}`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </div>
  );
}

/* ─────────────── Voice training preview ─────────────── */

export function VoicePreview() {
  return (
    <div className="rounded-2xl border border-ink-200/70 bg-white shadow-md">
      <FakeWindowChrome title="BrandEcho · Voices" />
      <div className="space-y-4 p-5">
        <div className="rounded-xl border border-ink-200/60 bg-ink-50 p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-ink-900">My X · primary</p>
            <span className="badge-success">
              <Check className="h-3 w-3" />
              Ready
            </span>
          </div>
          <p className="mt-1 text-2xs text-ink-500">
            7 samples · 4,612 chars · trained 2 days ago
          </p>
          <div className="mt-3">
            <div className="flex items-center gap-2">
              <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-200">
                <div className="h-full w-[82%] rounded-full bg-emerald-500" />
              </div>
              <span className="text-xs font-medium text-ink-700">
                82 / Strong
              </span>
            </div>
          </div>
        </div>
        <VoiceTrait label="Sentence rhythm" value="Punchy · 12 words avg" />
        <VoiceTrait label="Openers" value="Hook · contrarian + listicle" />
        <VoiceTrait label="Mood" value="Direct · slightly sardonic" />
        <VoiceTrait label="Punctuation" value="Em-dash heavy · no semicolons" />
      </div>
    </div>
  );
}

function VoiceTrait({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-ink-100 px-3 py-2.5">
      <span className="text-2xs font-semibold uppercase tracking-wider text-ink-400">
        {label}
      </span>
      <span className="text-sm font-medium text-ink-800">{value}</span>
    </div>
  );
}

/* ─────────────── Platform outputs grid ─────────────── */

export function PlatformOutputsPreview() {
  return (
    <div className="rounded-2xl border border-ink-200/70 bg-white shadow-md">
      <FakeWindowChrome title="BrandEcho · Variants" />
      <div className="grid gap-3 p-5 sm:grid-cols-2">
        <BigPreview
          accent="bg-ink-900"
          platform="X / Twitter"
          subtitle="Auto-thread · 5 cards"
          lines={[
            "Most teams ship slow because they don't define 'done'.",
            "1/ Without a written definition, every dev defines it…",
            "2/ Reviewers nitpick because there's no contract…",
            "3/ QA finds 'bugs' that aren't bugs because the spec…",
          ]}
        />
        <BigPreview
          accent="bg-blue-700"
          platform="LinkedIn"
          subtitle="Long-form · 1,840 chars"
          lines={[
            "Most teams aren't actually slow.",
            "They're undefined.",
            "Here's what shifted when we wrote down 'done':",
            "→ Reviews dropped from 4 days to 9 hours.",
          ]}
        />
        <BigPreview
          accent="bg-sky-500"
          platform="Bluesky"
          subtitle="Single post · 287 chars"
          lines={[
            "Most teams ship slow because they",
            "don't define 'done'. Write it down,",
            "watch review time drop 80%.",
          ]}
        />
        <BigPreview
          accent="bg-rose-500"
          platform="Short-video"
          subtitle="60s script · hook+body+CTA"
          lines={[
            "[Hook · 5s] Most teams aren't slow.",
            "[Body · 40s] What 3 sentences did.",
            "[CTA · 5s] Pin it in your team doc.",
          ]}
        />
      </div>
    </div>
  );
}

function BigPreview({
  accent,
  platform,
  subtitle,
  lines,
}: {
  accent: string;
  platform: string;
  subtitle: string;
  lines: string[];
}) {
  return (
    <div className="rounded-xl border border-ink-200/60 bg-white p-4 transition-all hover:-translate-y-0.5 hover:shadow-sm">
      <div className="mb-3 flex items-center gap-2">
        <span className={`h-1.5 w-7 rounded-full ${accent}`} />
        <div>
          <p className="text-sm font-semibold text-ink-900">{platform}</p>
          <p className="text-2xs text-ink-500">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-1.5 border-l-2 border-ink-100 pl-3">
        {lines.map((l, i) => (
          <p key={i} className="text-xs leading-relaxed text-ink-700">
            {l}
          </p>
        ))}
      </div>
    </div>
  );
}

/* ─────────────── Automation preview ─────────────── */

export function AutomationPreview() {
  return (
    <div className="space-y-3">
      <AutomationCard
        Icon={CalendarClock}
        title="Queue · weekly slots"
        subtitle="Mon 09:00 · Wed 12:00 · Fri 18:00"
        bar={62}
        badge="3 active"
      />
      <AutomationCard
        Icon={RotateCcw}
        title="Evergreen · AI rewrite"
        subtitle="Cadence 14d · 6 reposts so far"
        bar={43}
        badge="Running"
        tone="success"
      />
      <AutomationCard
        Icon={Repeat2}
        title="Auto-retweet · 0 AI cost"
        subtitle="Threshold 50 likes · weekly"
        bar={88}
        badge="Last run 3d"
      />
      <AutomationCard
        Icon={Sparkles}
        title="Auto-plug · voice-matched"
        subtitle="Delay 4h · plug → blog.example"
        bar={75}
        badge="2 active"
      />
    </div>
  );
}

function AutomationCard({
  Icon,
  title,
  subtitle,
  bar,
  badge,
  tone = "neutral",
}: {
  Icon: LucideIcon;
  title: string;
  subtitle: string;
  bar: number;
  badge: string;
  tone?: "neutral" | "success";
}) {
  return (
    <div className="rounded-xl border border-ink-200/60 bg-white p-4 shadow-xs transition-all hover:-translate-y-0.5 hover:shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand-gradient-soft text-brand-600">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className="truncate text-sm font-semibold text-ink-900">
              {title}
            </p>
            <span
              className={
                tone === "success" ? "badge-success" : "badge-neutral"
              }
            >
              {badge}
            </span>
          </div>
          <p className="mt-0.5 text-2xs text-ink-500">{subtitle}</p>
          <div className="mt-2.5 h-1 overflow-hidden rounded-full bg-ink-100">
            <div
              className="h-full rounded-full bg-brand-gradient"
              style={{ width: `${bar}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
